import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Layers } from "lucide-react";
import { api, errMsg, fmtDate, fmtMoney } from "../../lib/api";
import { BatchOrderDialog } from "../../components/BatchOrderDialog";
import { PageHeader, DataTable, Td, FormDialog, Field, TextInput, SelectInput, ActionButton, EmptyState } from "../../components/common";

const EMPTY = { customer_id: "", barrels: "", rate_per_barrel: "", notes: "" };

export default function SwdSalesOrders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/swd/sales-orders").then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const total = (Number(form.barrels) || 0) * (Number(form.rate_per_barrel) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { customer_id: form.customer_id, barrels: Number(form.barrels), rate_per_barrel: Number(form.rate_per_barrel) };
      if (form.notes) payload.notes = form.notes;
      await api.post("/swd/sales-orders", payload);
      toast.success("Sales order created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (o, status) => {
    try {
      await api.patch(`/swd/sales-orders/${o.id}`, { status });
      toast.success(`${o.order_number} → ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="swd-orders-page">
      <PageHeader title="Hauling Sales Orders" subtitle="Rate × quantity of barrels — confirmed orders flow into Accounting Logic" testId="swd-orders-header">
        <ActionButton testId="swd-create-batch-button" variant="secondary" onClick={() => setBatchOpen(true)}>
          <Layers size={16} /> New Batch
        </ActionButton>
        <ActionButton testId="swd-create-order-button" onClick={() => setOpen(true)}>
          <Plus size={16} /> New Order
        </ActionButton>
      </PageHeader>

      {orders.length === 0 ? (
        <EmptyState message="No sales orders yet" testId="swd-orders-empty" />
      ) : (
        <DataTable
          testId="swd-orders-table"
          columns={["Order", "Customer", "Barrels", "Rate/BBL", "Total", "Status", "Created"]}
          rows={orders}
          renderRow={(o) => (
            <tr key={o.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`swd-order-row-${o.id}`}>
              <Td className="font-mono text-amber-400">{o.order_number}</Td>
              <Td className="font-medium text-slate-100">{o.customer_name}</Td>
              <Td className="font-mono">{o.barrels}</Td>
              <Td className="font-mono">{fmtMoney(o.rate_per_barrel)}</Td>
              <Td className="font-mono text-emerald-400">{fmtMoney(o.total)}</Td>
              <Td>
                <SelectInput
                  testId={`swd-order-status-${o.id}`}
                  value={o.status}
                  onChange={(e) => setStatus(o, e.target.value)}
                  options={["draft", "confirmed", "completed", "cancelled"].map((s) => ({ value: s, label: s.toUpperCase() }))}
                  className="!w-32 !py-1 !text-xs !bg-white !text-slate-100"
                />
              </Td>
              <Td className="text-slate-500 text-xs">{fmtDate(o.created_at)}</Td>
            </tr>
          )}
        />
      )}

      <BatchOrderDialog open={batchOpen} onOpenChange={setBatchOpen} onSaved={load} />

      <FormDialog open={open} onOpenChange={setOpen} title="New Hauling Sales Order" testId="swd-order-dialog">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Customer">
            <SelectInput testId="swd-order-customer-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required placeholder="Select customer" options={[...customers].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name }))} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Quantity (barrels)">
              <TextInput testId="swd-order-barrels-input" type="number" step="0.1" min="0" value={form.barrels} onChange={(e) => setForm({ ...form, barrels: e.target.value })} required />
            </Field>
            <Field label="Rate per barrel ($)">
              <TextInput testId="swd-order-rate-input" type="number" step="0.01" min="0" value={form.rate_per_barrel} onChange={(e) => setForm({ ...form, rate_per_barrel: e.target.value })} required />
            </Field>
          </div>
          <div className="bg-slate-900/60 border border-slate-700 rounded-md px-4 py-3 flex justify-between items-center" data-testid="swd-order-total-preview">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Order total</span>
            <span className="font-heading text-2xl font-bold text-amber-400">{fmtMoney(total)}</span>
          </div>
          <Field label="Notes">
            <TextInput testId="swd-order-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <ActionButton testId="swd-order-submit-button" type="submit" disabled={busy} className="w-full justify-center">
            {busy ? "Creating…" : "Create Order"}
          </ActionButton>
        </form>
      </FormDialog>
    </div>
  );
}
